import { ApiClient, PaginatedResponse } from './apiClient';
import type { 
  ReporteFiltros,
  SolicitudGeneracionPDF,
  RespuestaGeneracionPDF,
  KMLUploadResponse
} from '../types';
import type { UploadedKml } from './kml/kmlService';

// ===============================
// TIPOS PARA OPERACIÓN ATÓMICA
// ===============================

// Estructura de datos del frontend (ReporteContext)
export interface ReporteEnMemoria {
  numero_reporte: string;
  maquina_id: number | null;
  fecha_reporte: string;
  operador_id: number | null;
  horometro_inicial: number;
  horometro_final: number;
  horas_trabajadas: number;
  faenas: FaenaEnMemoria[];
  estado_maquina: 'OPERATIVA' | 'FALLA_MENOR' | 'FALLA_MAYOR' | 'MANTENIMIENTO';
  observaciones: string;
  novedades: string;
  insumos: InsumoEnMemoria[];
  items_chequeo: ItemChequeoEnMemoria[];
}

export interface CalificadorFaenaRequest {
  calificador_id: number;
  valor: string;
}

export interface FaenaEnMemoria {
  id_temporal: string;
  faena_id: number | null;
  predio_id: number | null;
  rodal_id: number | null;
  horas: number;
  area_trabajada: number;
  archivos_kml: UploadedKml[];
  calificadores: CalificadorFaenaRequest[];
  descripcion: string;
  observaciones?: string;
}

export interface InsumoEnMemoria {
  id_temporal: string;
  insumo_id: number;
  cantidad: number;
  nombre_insumo?: string;
  unidad?: string;
  costo_unitario?: number;
}

export interface ItemChequeoEnMemoria {
  id_temporal: string;
  item_chequeo_id: number;
  nombre_item?: string;
  verificado: boolean;
  horometro: number;   // Horómetro cuando se hizo el chequeo (requerido por backend)
  tiempo: number;      // Minutos que tomó el chequeo (requerido por backend, 1-480)
  observaciones?: string;
  requiere_atencion?: boolean;
}

// Estructura que espera el backend
export interface ReporteCreateRequest {
  numero: string;
  fecha: string;
  maquina_id: number;
  operador_id?: number | null;
  horometro_inicial: number;
  horometro_final: number;
  observaciones?: string;
  estado_maquina?: string;
  novedades?: string;
  faenas: ReporteFaenaCreateRequest[];
  insumos: ReporteInsumoCreateRequest[];
  items_chequeo: ReporteItemChequeoCreateRequest[];
}

export interface ReporteFaenaCreateRequest {
  faena_id: number;
  predio_id?: number | null;
  rodal_id?: number | null;
  horas: number;
  area?: number;
  archivos_kml: ArchivoKmlCreateRequest[];
  calificadores: CalificadorFaenaRequest[];
}

export interface ArchivoKmlCreateRequest {
  nombre_archivo: string;
  area_hectareas: number;
  datos_kml?: string; // base64 encoded - opcional para KMLs existentes
  archivo_id?: number; // ID de archivo existente - para edición
}

export interface ReporteInsumoCreateRequest {
  insumo_id: number;
  cantidad: number;
}

export interface ReporteItemChequeoCreateRequest {
  itemchequeo_id: number;
  horometro: number;
  tiempo: number; // minutos
  observaciones?: string;
}

// Respuesta del backend
export interface ReporteCreateResponse {
  success: boolean;
  data: {
    id: number;
    numero: string;
    fecha: string;
    maquina_id: number;
    faenas: {
      id: number;
      faena_id: number;
      archivos_kml: {
        id: number;
        nombre_archivo: string;
        area_hectareas: number;
      }[];
    }[];
  };
  message: string;
}

export interface ProgressCallback {
  (paso: string, progreso: number): void;
}

export interface ValidacionError {
  campo: string;
  mensaje: string;
}

// ===============================
// TIPOS REALES DEL BACKEND
// ===============================

export interface ReporteBackend {
  id: number;
  numero: string;
  fecha: string;
  maquina_codigo: string;
  maquina_descripcion: string;
  operador_codigo: string;
  operador_descripcion: string;
  estado: string;
  horas_trabajadas: number;
  total_faenas: number;
  total_area_trabajada: number;
  total_longitud_metros: number; // ✅ NUEVO campo para longitud
  tiene_ubicacion: boolean;
  created_at: string;
}

export class ReportesService {
  /**
   * Obtiene el siguiente número de reporte electrónico disponible
   */
  static async obtenerSiguienteNumero(): Promise<string> {
    try {
      const response = await ApiClient.get<{numero: string}>('/reportes/siguiente-numero');
      
      if (response.numero) {
        return response.numero;
      }
      
      throw new Error('Respuesta inválida del servidor');
    } catch (error: any) {
      console.error('❌ Error al obtener siguiente número:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener siguiente número';
      
      throw new Error(errorMessage);
    }
  }

  // ===============================
  // OPERACIÓN ATÓMICA (CHECKPOINT 5)
  // ===============================

  /**
   * Operación atómica: Envía reporte completo del frontend al backend
   * Maneja transformación de datos, validación y envío en una sola transacción
   */
  static async crearReporteCompleto(
    reporteMemoria: ReporteEnMemoria,
    empresaId: number,
    onProgress?: ProgressCallback
  ): Promise<ReporteCreateResponse> {
    try {
      // PASO 1: Validación pre-envío
      onProgress?.('Validando datos del reporte...', 10);
      const erroresValidacion = this.validarReporteCompleto(reporteMemoria, empresaId);
      if (erroresValidacion.length > 0) {
        throw new Error(`Errores de validación: ${erroresValidacion.map(e => e.mensaje).join(', ')}`);
      }

      // PASO 2: Transformación de datos
      onProgress?.('Transformando datos para el backend...', 25);
      const reporteBackend = await this.transformarReporteParaBackendPrivada(reporteMemoria);

      // PASO 3: Envío atómico
      onProgress?.('Enviando reporte al servidor...', 50);
      
      const response = await ApiClient.post<ReporteCreateResponse>('/reportes', reporteBackend);
      
      // PASO 4: Validación de respuesta
      onProgress?.('Validando respuesta del servidor...', 75);
      
      // El backend retorna CreateUpdateResponse<ReporteData> directamente
      let actualResponse: ReporteCreateResponse;
      
      if (response && typeof response === 'object') {
        // Si la respuesta tiene estructura estándar
        if ('success' in response && 'data' in response) {
          actualResponse = response as unknown as ReporteCreateResponse;
        } else {
          // Si el backend devuelve directamente el reporte (fallback)
          actualResponse = {
            success: true,
            data: {
              id: (response as any).id,
              numero: (response as any).numero,
              fecha: (response as any).fecha,
              maquina_id: (response as any).maquina_id,
              faenas: (response as any).faenas || []
            },
            message: 'Reporte creado exitosamente'
          };
        }
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
      
      if (!actualResponse.success || !actualResponse.data) {
        throw new Error(actualResponse.message || 'Error en la respuesta del servidor');
      }

      onProgress?.('Reporte creado exitosamente', 100);
      
      return actualResponse;

    } catch (error: any) {
      console.error('❌ Error en operación atómica:', error);
      
      // Extraer mensaje de error más descriptivo
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al crear reporte completo';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Validación integral del reporte antes del envío
   */
  private static validarReporteCompleto(
    reporte: ReporteEnMemoria, 
    empresaId: number
  ): ValidacionError[] {
    const errores: ValidacionError[] = [];

    // Validar empresa
    if (!empresaId) {
      errores.push({ campo: 'empresa', mensaje: 'No hay empresa activa' });
    }

    // Validar datos básicos
    if (!reporte.numero_reporte?.trim()) {
      errores.push({ campo: 'numero_reporte', mensaje: 'Número de reporte es requerido' });
    }

    if (!reporte.maquina_id) {
      errores.push({ campo: 'maquina_id', mensaje: 'Máquina es requerida' });
    }

    if (!reporte.fecha_reporte) {
      errores.push({ campo: 'fecha_reporte', mensaje: 'Fecha de reporte es requerida' });
    }

    // Validar horómetros
    if (reporte.horometro_final <= reporte.horometro_inicial) {
      errores.push({ 
        campo: 'horometros', 
        mensaje: 'Horómetro final debe ser mayor al inicial' 
      });
    }

    // Validar faenas
    if (!reporte.faenas || reporte.faenas.length === 0) {
      errores.push({ campo: 'faenas', mensaje: 'Debe haber al menos una faena' });
    }

    reporte.faenas.forEach((faena, index) => {
      if (!faena.faena_id) {
        errores.push({ 
          campo: `faenas[${index}].faena_id`, 
          mensaje: `Faena ${index + 1}: Debe seleccionar una faena` 
        });
      }

      if (faena.horas <= 0) {
        errores.push({ 
          campo: `faenas[${index}].horas`, 
          mensaje: `Faena ${index + 1}: Horas trabajadas debe ser mayor a 0` 
        });
      }

      if (faena.area_trabajada < 0) {
        errores.push({ 
          campo: `faenas[${index}].area_trabajada`, 
          mensaje: `Faena ${index + 1}: Área trabajada no puede ser negativa` 
        });
      }

      // Validar archivos KML si existen
      faena.archivos_kml.forEach((kml, kmlIndex) => {
        if (!kml.nombre_original) {
          errores.push({ 
            campo: `faenas[${index}].archivos_kml[${kmlIndex}]`, 
            mensaje: `Faena ${index + 1}, KML ${kmlIndex + 1}: Nombre de archivo es requerido` 
          });
        }

        if (!kml.file && !kml.area_hectareas) {
          errores.push({ 
            campo: `faenas[${index}].archivos_kml[${kmlIndex}]`, 
            mensaje: `Faena ${index + 1}, KML ${kmlIndex + 1}: Archivo KML o área es requerida` 
          });
        }
      });
    });

    return errores;
  }

  /**
   * Transforma datos del frontend al formato que espera el backend
   * ✅ NUEVA: Versión pública para uso en FormularioReporteCompleto
   */
  static async transformarReporteParaBackend(
    reporte: ReporteEnMemoria
  ): Promise<ReporteCreateRequest> {
    
    // Transformar faenas con KMLs
    const faenasTransformadas = await this.transformarFaenas(reporte.faenas);
    
    // Transformar insumos
    const insumosTransformados = this.transformarInsumos(reporte.insumos);
    
    // Transformar items de chequeo
    const itemsChequeoTransformados = this.transformarItemsChequeo(reporte.items_chequeo);

    return {
      numero: reporte.numero_reporte,
      fecha: reporte.fecha_reporte,
      maquina_id: reporte.maquina_id!,
      operador_id: reporte.operador_id || null,
      horometro_inicial: reporte.horometro_inicial,
      horometro_final: reporte.horometro_final,
      observaciones: reporte.observaciones || undefined,
      estado_maquina: reporte.estado_maquina,
      novedades: reporte.novedades || undefined,
      faenas: faenasTransformadas,
      insumos: insumosTransformados,
      items_chequeo: itemsChequeoTransformados
    };
  }

  /**
   * Transforma datos del frontend al formato que espera el backend (PRIVADA)
   */
  private static async transformarReporteParaBackendPrivada(
    reporte: ReporteEnMemoria
  ): Promise<ReporteCreateRequest> {
    
    // Transformar faenas con KMLs
    const faenasTransformadas = await this.transformarFaenas(reporte.faenas);
    
    // Transformar insumos
    const insumosTransformados = this.transformarInsumos(reporte.insumos);
    
    // Transformar items de chequeo
    const itemsChequeoTransformados = this.transformarItemsChequeo(reporte.items_chequeo);

    return {
      numero: reporte.numero_reporte,
      fecha: reporte.fecha_reporte,
      maquina_id: reporte.maquina_id!,
      operador_id: reporte.operador_id || null,
      horometro_inicial: reporte.horometro_inicial,
      horometro_final: reporte.horometro_final,
      observaciones: reporte.observaciones || undefined,
      estado_maquina: reporte.estado_maquina,
      novedades: reporte.novedades || undefined,
      faenas: faenasTransformadas,
      insumos: insumosTransformados,
      items_chequeo: itemsChequeoTransformados
    };
  }

  /**
   * Transforma faenas del frontend al formato backend
   * ✅ SIMPLIFICADO: Procesa TODOS los KML del contexto (contexto completo)
   */
  private static async transformarFaenas(
    faenas: FaenaEnMemoria[]
  ): Promise<ReporteFaenaCreateRequest[]> {
    const faenasTransformadas: ReporteFaenaCreateRequest[] = [];
    
    for (const faena of faenas) {
      // Procesar TODOS los archivos KML del contexto
      const archivosKmlTransformados: ArchivoKmlCreateRequest[] = [];
      

      
      for (const kml of faena.archivos_kml) {
        // PRIORIDAD 1: Si tiene archivo_id < 1000000000000 (ID real del backend)
        // Los IDs del backend son secuenciales desde 1 (ej: 561)
        // Los IDs temporales de uploadTemporal usan Date.now() que es > 1700000000000
        // IMPORTANTE: En modo edición, los KMLs existentes tienen TANTO archivo_id COMO file (descargado)
        if (kml.archivo_id && kml.archivo_id < 1000000000000 && kml.nombre_original) {
          // 🗃️ KML EXISTENTE DEL BACKEND: Enviar solo referencia (sin datos_kml)
          archivosKmlTransformados.push({
            archivo_id: kml.archivo_id,
            nombre_archivo: kml.nombre_original,
            area_hectareas: kml.area_hectareas || 0
            // NO incluir datos_kml - señal de que es un archivo existente
          });
          // console.log(`ℹ️ KML existente referenciado: ${kml.nombre_original} (ID backend: ${kml.archivo_id})`);
        }
        // PRIORIDAD 2: Si NO tiene archivo_id válido del backend pero SÍ tiene File (es un KML nuevo)
        else if (kml.file && kml.file instanceof File) {
          // 📁 KML NUEVO CON ARCHIVO: Procesar y enviar
          const datosKmlBase64 = await this.fileToBase64(kml.file);

          archivosKmlTransformados.push({
            nombre_archivo: kml.nombre_original,
            area_hectareas: kml.area_hectareas || 0,
            datos_kml: datosKmlBase64
          });
          // console.log(`📁 KML nuevo procesado: ${kml.nombre_original} (${kml.area_hectareas} ha)`);
        }
        // CASO NO VÁLIDO: Sin file y sin ID válido del backend
        else {
          console.warn(`⚠️ KML omitido: ${kml.nombre_original} (sin archivo ni ID válido del backend, archivo_id: ${kml.archivo_id})`);
        }
      }
      


      faenasTransformadas.push({
        faena_id: faena.faena_id!,
        predio_id: faena.predio_id,
        rodal_id: faena.rodal_id,
        horas: faena.horas,
        area: faena.area_trabajada,
        archivos_kml: archivosKmlTransformados,
        calificadores: (faena.calificadores || []).map(c => ({
          calificador_id: c.calificador_id,
          valor: c.valor,
        })),
      });
    }
    
    return faenasTransformadas;
  }

  /**
   * Transforma insumos del frontend al formato backend
   */
  private static transformarInsumos(
    insumos: InsumoEnMemoria[]
  ): ReporteInsumoCreateRequest[] {
    return insumos.map(insumo => ({
      insumo_id: insumo.insumo_id,
      cantidad: insumo.cantidad
    }));
  }

  /**
   * Transforma items de chequeo del frontend al formato backend
   * Solo envía items que están verificados (chequeados)
   */
  private static transformarItemsChequeo(
    items: ItemChequeoEnMemoria[]
  ): ReporteItemChequeoCreateRequest[] {
    // Filtrar solo items verificados
    return items
      .filter(item => item.verificado)
      .map(item => ({
        itemchequeo_id: item.item_chequeo_id,
        horometro: item.horometro || 0,
        tiempo: item.tiempo || 1, // Mínimo 1 minuto si no está definido
        observaciones: item.observaciones
      }));
  }


  /**
   * Convierte un archivo a base64
   */
  private static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remover el prefijo "data:application/octet-stream;base64,"
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Error al convertir archivo a base64'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer archivo'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  // ===============================
  // CRUD BÁSICO DE REPORTES
  // ===============================

  /**
   * Obtiene una lista paginada de reportes con filtros
   */
  static async getReportes(filtros?: ReporteFiltros): Promise<PaginatedResponse<ReporteBackend>> {
    try {
      const params = new URLSearchParams();
      
      if (filtros) {
        Object.entries(filtros).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const url = `/reportes${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Usar método especializado para respuestas paginadas
      return await ApiClient.getPaginated<ReporteBackend>(url);
      
    } catch (error: any) {
      console.error('❌ Error al obtener reportes:', error);
      
      // Extraer mensaje de error más descriptivo
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener reportes';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Obtiene un reporte por ID
   */
  static async getReporte(id: number): Promise<any> {
    try {
      // Usar método especializado para objetos individuales
      return await ApiClient.getOne<any>(`/reportes/${id}`);
      
    } catch (error: any) {
      console.error('❌ Error al obtener reporte:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener reporte';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Crea un nuevo reporte
   */
  static async crearReporte(data: any): Promise<ReporteBackend> {
    try {
      // POST retorna CreateUpdateResponse<ReporteBackend> directamente
      const response = await ApiClient.post<ReporteBackend>('/reportes', data);
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Error al crear reporte');
    } catch (error: any) {
      console.error('❌ Error al crear reporte:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al crear reporte';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Actualiza un reporte existente
   */
  static async actualizarReporte(id: number, data: any): Promise<any> {
    try {
      // PUT retorna CreateUpdateResponse<ReporteData> directamente
      const response = await ApiClient.put<any>(`/reportes/${id}`, data);
      
      // Verificar si es el nuevo formato estándar
      if (response.success && response.data) {
        return response.data; // Devolver solo los datos del reporte
      }
      
      // Fallback: Si todavía es el formato antiguo (directo)
      if ((response as any).id && (response as any).numero) {
        return response;
      }
      
      // Si no tiene la estructura esperada, asumir que es válido si no hay errores
      return response;
      
    } catch (error: any) {
      console.error('❌ Error al actualizar reporte:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al actualizar reporte';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Elimina un reporte existente
   */
  static async eliminarReporte(id: number): Promise<void> {
    try {
      // DELETE retorna {message: string} directamente
      const response = await ApiClient.delete(`/reportes/${id}`);
      
      // El backend devuelve {message: "Reporte eliminado exitosamente"}
      // No necesitamos verificar success, si llega acá es porque fue exitoso
      if (response && ((response as any).message || (response as any).detail)) {
        return; // Eliminación exitosa
      }
      
      // Si no hay mensaje, asumir éxito (status 200)
      return;
      
    } catch (error: any) {
      console.error('❌ Error al eliminar reporte:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al eliminar reporte';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Sube un archivo KML/KMZ
   */
  static async subirArchivoKML(reporteId: number, file: File): Promise<KMLUploadResponse> {
    try {
      const response = await ApiClient.upload<any>(
        '/kml/upload',
        file,
        { reporte_id: reporteId }
      );

      if (response?.success && response?.data) {
        return response.data;
      }

      throw new Error(response?.message || 'Error al subir archivo KML');
    } catch (error) {
      console.error('❌ Error al subir archivo KML:', error);
      throw error;
    }
  }

  /**
   * Genera un PDF de un reporte
   */
  static async generarPDF(reporteId: number, opciones?: SolicitudGeneracionPDF): Promise<RespuestaGeneracionPDF> {
    try {
      const requestData = {
        reporte_id: reporteId,
        incluir_mapa: true,
        zoom_mapa: 15,
        ...opciones
      };

      const response = await ApiClient.post<RespuestaGeneracionPDF>(
        `/reportes/${reporteId}/pdf`,
        requestData
      );
      
      if ((response as any)?.success && (response as any)?.data) {
        return (response as any).data;
      }

      throw new Error((response as any)?.message || 'Error al generar PDF');
    } catch (error) {
      console.error('❌ Error al generar PDF:', error);
      throw error;
    }
  }

  /**
   * Descarga un PDF de un reporte
   */
  static async descargarPDF(
    reporteId: number,
    opciones: {
      incluirMapa?: boolean;
      zoom?: number;
      lang?: string;
    } = {}
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (opciones.incluirMapa !== undefined) {
        params.append('include_map', opciones.incluirMapa.toString());
      }
      if (opciones.zoom !== undefined) {
        params.append('zoom', opciones.zoom.toString());
      }
      if (opciones.lang) {
        params.append('lang', opciones.lang);
      }

      const url = `/reportes/${reporteId}/pdf${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Usar ApiClient para obtener la URL base correcta y headers
      const baseURL = ApiClient.getBaseURL();
      const token = ApiClient.getAuthToken();
      const empresaId = ApiClient.getEmpresaId();
      
      const response = await fetch(`${baseURL}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaId || '1',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      return await response.blob();
      
    } catch (error: any) {
      console.error('❌ Error al descargar PDF:', error);
      
      const errorMessage = error?.message || 'Error al descargar PDF';
      throw new Error(errorMessage);
    }
  }
}

export default ReportesService;
